/**
 * Main history management system with undo/redo functionality
 */

import { IHistoryManager, IMapCommand, IHistoryConfig } from './types';

export class HistoryManager implements IHistoryManager {
  private undoStack: IMapCommand[] = [];
  private redoStack: IMapCommand[] = [];
  private maxHistorySize: number;
  private enabled: boolean;

  private eventTarget: EventTarget;

  constructor(config: IHistoryConfig = {}) {
    this.enabled = config.enabled ?? true;
    this.maxHistorySize = config.maxHistorySize ?? 50;

    this.eventTarget = new EventTarget();

    if (config.enableKeyboardShortcuts ?? true) {
      this.setupKeyboardShortcuts();
    }
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  get canUndo(): boolean {
    return this.enabled && this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.enabled && this.redoStack.length > 0;
  }

  get undoCount(): number {
    return this.undoStack.length;
  }

  get redoCount(): number {
    return this.redoStack.length;
  }

  enable(): void {
    this.enabled = true;
    this.notifyHistoryChange();
  }

  disable(): void {
    this.enabled = false;
    this.notifyHistoryChange();
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.notifyHistoryChange();

    this.dispatchHistoryEvent('clear');
  }

  executeCommand(command: IMapCommand): void {
    if (!this.enabled) {
      command.execute();
      return;
    }

    if (!command.canExecute()) {
      console.warn('Command cannot be executed:', command.description);
      return;
    }

    try {
      command.execute();
      this.undoStack.push(command);
      this.redoStack = [];

      if (this.undoStack.length > this.maxHistorySize) {
        this.undoStack.shift();
      }

      this.notifyHistoryChange();

      this.dispatchHistoryEvent('execute', command);
      console.log(`✅ Executed: ${command.description}`);
    } catch (error) {
      console.error('Error executing command:', error);
    }
  }

  undo(): boolean {
    if (!this.canUndo) {
      return false;
    }

    const command = this.undoStack.pop()!;

    if (!command.canUndo()) {
      console.warn('Command cannot be undone:', command.description);
      return false;
    }

    try {
      command.undo();
      this.redoStack.push(command);
      this.notifyHistoryChange();

      this.dispatchHistoryEvent('undo', command);

      console.log(`↶ Undone: ${command.description}`);
      return true;
    } catch (error) {
      console.error('Error undoing command:', error);
      this.undoStack.push(command);
      return false;
    }
  }

  redo(): boolean {
    if (!this.canRedo) {
      return false;
    }

    const command = this.redoStack.pop()!;

    if (!command.canExecute()) {
      console.warn('Command cannot be redone:', command.description);
      return false;
    }

    try {
      command.execute();
      this.undoStack.push(command);
      this.notifyHistoryChange();

      this.dispatchHistoryEvent('redo', command);

      console.log(`↷ Redone: ${command.description}`);
      return true;
    } catch (error) {
      console.error('Error redoing command:', error);
      this.redoStack.push(command);
      return false;
    }
  }

  getUndoDescription(): string | null {
    if (!this.canUndo) return null;
    return this.undoStack[this.undoStack.length - 1].description;
  }

  getRedoDescription(): string | null {
    if (!this.canRedo) return null;
    return this.redoStack[this.redoStack.length - 1].description;
  }

  addEventListener(type: string, listener: EventListener): void {
    this.eventTarget.addEventListener(type, listener);
  }

  removeEventListener(type: string, listener: EventListener): void {
    this.eventTarget.removeEventListener(type, listener);
  }

  private dispatchHistoryEvent(
    type: 'undo' | 'redo' | 'execute' | 'clear',
    command?: IMapCommand
  ): void {
    const event = new CustomEvent(`history:${type}`, {
      detail: {
        type,
        command,
        canUndo: this.canUndo,
        canRedo: this.canRedo,
        undoCount: this.undoCount,
        redoCount: this.redoCount,
        timestamp: Date.now(),
      },
    });

    this.eventTarget.dispatchEvent(event);
    console.log(`🔔 History event dispatched: history:${type}`, event.detail);
  }

  private setupKeyboardShortcuts(): void {
    if (typeof window === 'undefined') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!this.enabled) return;

      // Ctrl+Z or Cmd+Z for undo
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        this.undo();
      }

      // Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z for redo
      if (
        ((event.ctrlKey || event.metaKey) && event.key === 'y') ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z')
      ) {
        event.preventDefault();
        this.redo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
  }

  private notifyHistoryChange(): void {
    // const state: IHistoryState = {
    //   undoStack: [...this.undoStack],
    //   redoStack: [...this.redoStack],
    //   maxHistorySize: this.maxHistorySize,
    //   isEnabled: this.enabled
    // };
  }
}
